import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface JourneyStage {
  id: string
  name: string
  customers: number
  conversion_rate: number
  avg_time_days: number
  drop_off_rate: number
}

interface Touchpoint {
  id: string
  channel: string
  stage: string
  interactions: number
  effectiveness: number
  sentiment: string
}

interface JourneyPath {
  id: string
  path: string[]
  frequency: number
  conversion_rate: number
  avg_duration_days: number
}

interface Statistics {
  total_journeys: number
  avg_journey_length: number
  completion_rate: number
  top_channel: string
}

export default function CustomerJourneyPage() {
  const [stages, setStages] = useState<JourneyStage[]>([])
  const [touchpoints, setTouchpoints] = useState<Touchpoint[]>([])
  const [paths, setPaths] = useState<JourneyPath[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [stagesRes, touchpointsRes, pathsRes, statsRes] = await Promise.all([
        fetch('/api/customerjourney/stages'),
        fetch('/api/customerjourney/touchpoints'),
        fetch('/api/customerjourney/paths'),
        fetch('/api/customerjourney/statistics')
      ])
      if (stagesRes.ok) setStages(await stagesRes.json())
      if (touchpointsRes.ok) setTouchpoints(await touchpointsRes.json())
      if (pathsRes.ok) setPaths(await pathsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching customer journey data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getSentimentColor = (sentiment: string) => {
    if (sentiment === 'positive') return 'text-green-600'
    if (sentiment === 'negative') return 'text-red-600'
    return 'text-yellow-600'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Customer Journey Mapping</h1>
          <p className="mt-2 text-gray-600">Phase 221 - Track and optimize customer journeys</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Journeys</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_journeys.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Journey Length</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.avg_journey_length} days</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Completion Rate</p>
              <p className="text-2xl font-bold text-green-600">{statistics.completion_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Top Channel</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.top_channel}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Journey Stages</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customers</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conversion</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Drop-off</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {stages.map((stage) => (
                  <tr key={stage.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{stage.name}</td>
                    <td className="px-6 py-4 text-gray-500">{stage.customers.toLocaleString()}</td>
                    <td className="px-6 py-4 text-green-600">{stage.conversion_rate}%</td>
                    <td className="px-6 py-4 text-gray-500">{stage.avg_time_days} days</td>
                    <td className="px-6 py-4 text-red-600">{stage.drop_off_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Touchpoint Analysis</h2>
            </div>
            <div className="p-6 space-y-4">
              {touchpoints.map((tp) => (
                <div key={tp.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{tp.channel}</p>
                    <p className="text-sm text-gray-500">{tp.stage}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-blue-600">{tp.interactions.toLocaleString()} interactions</p>
                    <p className={`text-sm ${getSentimentColor(tp.sentiment)}`}>
                      {tp.effectiveness}% effective
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Common Journey Paths</h2>
            </div>
            <div className="p-6 space-y-4">
              {paths.map((path) => (
                <div key={path.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex flex-wrap gap-2 mb-2">
                    {path.path.map((step, idx) => (
                      <span key={idx} className="flex items-center">
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-sm">{step}</span>
                        {idx < path.path.length - 1 && <span className="mx-1 text-gray-400">→</span>}
                      </span>
                    ))}
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">{path.frequency} customers</span>
                    <span className="text-green-600">{path.conversion_rate}% conversion</span>
                    <span className="text-blue-600">{path.avg_duration_days} days</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
